import { Box, Text } from "@modernatx-internal/component-library";
import React from "react";

import { useFinder } from "@/finder/hooks/V2/useFinder";
import type { FinderLocation } from "@/types/FinderLocation";

import { FinderListResult } from "./FinderListResult";
import { LoadMoreButton } from "./LoadMoreButton";

export type ScrollToListItem = (target: HTMLElement | number) => void;

interface FinderListProps {
  prefectureParam?: string;
}

export const FinderList: React.FC<FinderListProps> = ({ prefectureParam }) => {
  const {
    country,
    locationSelected,
    searching,
    searchId,
    visibleSearchResults,
    resultsView,
    noResultsFound,
    noResultsText,
    place
  } = useFinder();
  const locationRefs = React.useRef<(HTMLElement | null)[]>([]);
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const locations = React.useMemo(() => {
    if (resultsView === "mobileMap") {
      return locationSelected ? [locationSelected] : [];
    }
    return visibleSearchResults;
  }, [resultsView, locationSelected, visibleSearchResults]);

  const scrollToListItem = React.useCallback((target: HTMLElement | number) => {
    const listOffsetTop = listRef.current?.offsetTop || 0;
    const locationOffsetTop = typeof target === "number" ? target : target.offsetTop || 0;
    listRef.current?.scrollTo({
      top: locationOffsetTop - listOffsetTop,
      behavior: "smooth"
    });
  }, []);

  // When selected marker changes, run scrolling logic if possible.
  React.useEffect(() => {
    const refToScrollTo = locationRefs.current.find((refElement) => {
      // find the ref with the same name & address as selected location - there can be multiple "walgreens" so need to check address + name combo
      const customerName = locationSelected?.customer.name?.toLowerCase();
      const streetAddress = locationSelected?.location.streetAddress.toLowerCase();
      if (!customerName || !streetAddress) {
        return false;
      }
      return (
        refElement?.textContent?.toLowerCase().includes(customerName) &&
        refElement?.textContent.toLowerCase().includes(streetAddress)
      );
    });
    if (refToScrollTo) {
      scrollToListItem(refToScrollTo);
    }
  }, [locationSelected, scrollToListItem]);

  return (
    <Box
      // explicilty re-rendering loadMoreButton on search results change - this resets its state and refs for scrolling behavior.
      key={searchId}
      sx={{
        alignItems: "start",
        background: "white",
        borderRadius: ["initial", null, "radius-2"],
        boxShadow: ["none", null, "shadow-300"],
        display:
          !searching &&
          (place || (country === "jp" && prefectureParam && visibleSearchResults.length === 0))
            ? "flex"
            : "none",
        flexDirection: "column",
        flexGrow: [1, null, 0],
        flexShrink: [0, null, 1],
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
        paddingBottom: [0, null, "spacing-2"],
        paddingRight: [0, null, "spacing-2"],
        paddingTop: [0, null, "spacing-4"],
        pointerEvents: "all",
        width: "100%"
      }}
    >
      <Box
        ref={listRef}
        sx={{
          width: "100%",
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          background: "white",
          overflow: "auto",
          borderRadius: ["initial", null, "radius-2"],
          // WebKit scrollbar styling:
          "::-webkit-scrollbar": {
            width: "spacing-3"
          },
          "::-webkit-scrollbar-track": {
            background: "transparent"
          },
          "::-webkit-scrollbar-track:active": {
            background: "transparent"
          },
          "::-webkit-scrollbar-thumb": {
            backgroundColor: "stroke-neutral-tertiary",
            borderRadius: "radius-4"
          }
        }}
      >
        {noResultsFound ? (
          <Box
            sx={{
              borderRadius: ["unset", null, "radius-4"],
              textAlign: "center",
              height: "17",
              p: 4
            }}
          >
            <Text size="font-size-5" weight="bold" sx={{ p: "spacing-3" }}>
              {noResultsText}
            </Text>
          </Box>
        ) : (
          locations.map((location: FinderLocation, index: number) => (
            <FinderListResult
              key={`finder-list-result-${location.customer.id}`}
              location={location}
              setRef={(ref) => (locationRefs.current[index] = ref)}
            />
          ))
        )}
        <LoadMoreButton scrollToListItem={scrollToListItem} />
      </Box>
    </Box>
  );
};
